import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert, Avatar, Box, Button, Card, CircularProgress, Dialog, IconButton,
  Menu, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import TranslateIcon from "@mui/icons-material/Translate";
import { trackApi, settingsApi } from "../api/resources";
import { useLanguage } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";
import { COLORS } from "../theme";

const courierLabel = (p) => (p.courier === "Otro" && p.courierNote ? `Otro (${p.courierNote})` : p.courier);

export default function TrackingPage() {
  const { token } = useParams();
  const { t, lang, setLang } = useLanguage();
  const [pkg, setPkg] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publicSettings, setPublicSettings] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [langAnchor, setLangAnchor] = useState(null);
  const bottomRef = useRef(null);
  const autoLangSet = useRef(false);

  const load = () => {
    trackApi
      .get(token)
      .then((data) => {
        setPkg(data);
        setBlocked(false);
        // Al abrir por primera vez, hablar en el idioma preferente del
        // ocupante en vez del que detectó el navegador — es su link.
        if (!autoLangSet.current && data.occupantId?.preferredLanguage) {
          setLang(data.occupantId.preferredLanguage);
          autoLangSet.current = true;
        }
      })
      .catch((err) => {
        if (err.response?.status === 410) setBlocked(true);
        else if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    settingsApi.getPublic().then(setPublicSettings).catch(() => {});
  }, [token]); // eslint-disable-line

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [pkg?.messages?.length]);

  const fmtDateTime = (iso) =>
    new Date(iso).toLocaleString(lang === "en" ? "en-US" : "es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    setError("");
    try {
      const updated = await trackApi.sendMessage(token, text.trim());
      setPkg(updated);
      setText("");
    } catch (err) {
      if (err.response?.status === 410) setBlocked(true);
      else setError(err.response?.data?.error || t("tracking.errGeneric"));
    } finally {
      setSending(false);
    }
  };

  const LanguageSwitcher = () => (
    <>
      <IconButton size="small" onClick={(e) => setLangAnchor(e.currentTarget)} sx={{ color: COLORS.inkSoft }}>
        <TranslateIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={langAnchor} open={!!langAnchor} onClose={() => setLangAnchor(null)}>
        {LANGS.map((l) => (
          <MenuItem key={l.code} selected={l.code === lang} onClick={() => { setLang(l.code); setLangAnchor(null); }}>
            {l.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: COLORS.paper }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: COLORS.paper, p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 420, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>{t("tracking.notFoundTitle")}</Typography>
          <Typography variant="body2" color="text.secondary">{t("tracking.notFoundBody")}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.paper, py: { xs: 2, sm: 5 }, px: 2 }}>
      <Box sx={{ maxWidth: 640, mx: "auto" }}>
        <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 3 }}>
          {publicSettings?.logo ? (
            <Avatar variant="rounded" src={publicSettings.logo} sx={{ width: 32, height: 32 }} />
          ) : (
            <ApartmentIcon sx={{ color: COLORS.brass }} />
          )}
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            {publicSettings?.condoName || t("tracking.title")}
          </Typography>
          <LanguageSwitcher />
        </Stack>

        {blocked ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: COLORS.green, mb: 1 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>{t("tracking.deliveredTitle")}</Typography>
            <Typography variant="body2" color="text.secondary">{t("tracking.deliveredBody")}</Typography>
          </Paper>
        ) : (
          pkg && (
            <>
              <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="overline" color="text.secondary">{t("tracking.packageTitle")}</Typography>
                <Typography variant="h6" sx={{ mb: 2 }}>{pkg.desc}</Typography>
                <Stack spacing={1.2}>
                  <Row label={t("tracking.courier")} value={courierLabel(pkg)} />
                  {pkg.tracking && <Row label={t("tracking.guideNumber")} value={pkg.tracking} mono />}
                  <Row label={t("tracking.receivedAt")} value={fmtDateTime(pkg.receivedAt)} />
                  <Row label={t("tracking.receivedBy")} value={pkg.receivedBy} />
                </Stack>

                {pkg.photos?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      {t("common.photos")}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {pkg.photos.map((src, i) => (
                        <Box
                          key={i}
                          component="img"
                          src={src}
                          onClick={() => setLightbox(src)}
                          sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: 1, cursor: "pointer", border: `1px solid ${COLORS.line}` }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Card>

              <Card sx={{ p: 3 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  {t("tracking.conversationTitle")}
                </Typography>

                <Stack spacing={1.5} sx={{ maxHeight: 360, overflowY: "auto", mb: 2, pr: 0.5 }}>
                  {pkg.messages.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                      {t("tracking.noMessages")}
                    </Typography>
                  )}
                  {pkg.messages.map((m) => (
                    <Box
                      key={m._id}
                      sx={{
                        alignSelf: m.senderRole === "staff" ? "flex-start" : "flex-end",
                        maxWidth: "80%",
                        bgcolor: m.senderRole === "staff" ? "#F4F5EF" : COLORS.brassSoft,
                        borderRadius: 2,
                        px: 1.5, py: 1,
                      }}
                    >
                      <Typography variant="caption" fontWeight={600} sx={{ display: "block" }}>
                        {m.senderRole === "staff" ? m.senderName : t("tracking.you")}
                      </Typography>
                      <Typography variant="body2">{m.content}</Typography>
                      <Typography variant="caption" color="text.secondary">{fmtDateTime(m.sentAt)}</Typography>
                    </Box>
                  ))}
                  <div ref={bottomRef} />
                </Stack>

                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t("tracking.messagePlaceholder")}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    multiline
                    maxRows={4}
                  />
                  <Button variant="contained" onClick={handleSend} disabled={sending || !text.trim()} sx={{ minWidth: 44, px: 1.5 }}>
                    <SendIcon fontSize="small" />
                  </Button>
                </Stack>
                {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
              </Card>
            </>
          )
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 3 }}>
          {t("tracking.poweredBy")}
        </Typography>
      </Box>

      <Dialog open={!!lightbox} onClose={() => setLightbox(null)} maxWidth="sm">
        {lightbox && <Box component="img" src={lightbox} sx={{ maxWidth: "100%", maxHeight: "80vh" }} />}
      </Dialog>
    </Box>
  );
}

function Row({ label, value, mono }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} sx={mono ? { fontFamily: "monospace" } : undefined}>{value}</Typography>
    </Stack>
  );
}
